import { useEffect, useRef, useState, type FC } from 'react'
import { Trans } from 'react-i18next'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { alpha, darken, keyframes, lighten } from '@mui/material/styles'
import { PreparationMode } from '@/api'
import { intelligentPreparationViolet } from '@/components/AgenticPreparationIndicator'
import { DialogUpload } from '@/components/DialogUpload'
import {
  WhatsNewModal,
  type ChangelogReleasesState,
} from '@/components/WhatsNewModal'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flags'
import { usePathname } from '@/hooks/use-pathname'
import useTranslate from '@/hooks/use-translate'
import { useUserUiPreferences } from '@/hooks/use-user-ui-preferences'
import { captureAnnouncementEvent } from '@/lib/analytics/posthogAnalytics'
import { FeatureNames } from '@/lib/featureFlags'
import { tokens } from '@/locales/tokens'
import { paths } from '@/paths'

/** Clé de l'annonce dans `dismissedAnnouncements` (préférences d'interface, côté gateway). */
export const SMART_PREPARATION_ANNOUNCEMENT = 'smart_preparation'

/** Écrans d'accueil seulement : pas l'extraction, où le déclarant travaille. */
const BANNER_PATHS: readonly string[] = [
  paths.dashboard.index,
  paths.dashboard.documents.list,
]

/**
 * Effet d'arrivée, environ 8 s puis immobile : le dégradé respire deux fois pendant que le reflet
 * traverse la carte trois fois. Pas de boucle infinie dans un outil ouvert toute la journée.
 */
const gradientBreath = keyframes`
  0%, 100% { background-position: 100% 50%; }
  50% { background-position: 0% 50%; }
`

const sheen = keyframes`
  from { transform: translateX(-120%) skewX(-20deg); }
  to { transform: translateX(420%) skewX(-20deg); }
`

type SmartPreparationBannerProps = {
  /** Nouveautés publiées depuis nabu-console : « En savoir plus » ouvre la même modale que la sidenav. */
  changelog: ChangelogReleasesState
}

export const SmartPreparationBanner: FC<SmartPreparationBannerProps> = ({
  changelog,
}) => {
  const { isImpersonating } = useAuth()
  const isBannerEnabled = useFeatureFlag(FeatureNames.smartPreparationBanner)
  // « Essayer » ouvre la modale en préparation intelligente : sans elle, rien à annoncer.
  const isSmartPreparationEnabled = useFeatureFlag(
    FeatureNames.smartPreparation
  )
  const pathname = usePathname()
  const { preferences, hydrated, update } = useUserUiPreferences()
  const { t: tCommon } = useTranslate('common')
  // Fermé dans cette session : tient même si l'enregistrement échoue ou si la gateway l'ignore.
  const [closed, setClosed] = useState(false)
  const [wizardMounted, setWizardMounted] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const viewTracked = useRef(false)

  // `hydrated` : on attend les préférences du serveur, le cache local peut être celui d'un autre compte.
  const visible =
    isBannerEnabled &&
    isSmartPreparationEnabled &&
    hydrated &&
    !closed &&
    BANNER_PATHS.includes(pathname) &&
    !preferences.dismissedAnnouncements.includes(
      SMART_PREPARATION_ANNOUNCEMENT
    ) &&
    preferences.defaultPreparation !== PreparationMode.Intelligent

  useEffect(() => {
    if (!visible || viewTracked.current) return
    viewTracked.current = true
    captureAnnouncementEvent('viewed', SMART_PREPARATION_ANNOUNCEMENT)
  }, [visible])

  const dismiss = (): void => {
    setClosed(true)
    // En impersonation, le staff ne ferme pas l'annonce à la place du client.
    if (!isImpersonating) {
      void update({ dismissAnnouncement: SMART_PREPARATION_ANNOUNCEMENT })
    }
  }

  const handleClose = (): void => {
    captureAnnouncementEvent('dismissed', SMART_PREPARATION_ANNOUNCEMENT)
    dismiss()
  }

  const handleTry = (): void => {
    captureAnnouncementEvent('cta_clicked', SMART_PREPARATION_ANNOUNCEMENT)
    dismiss()
    setWizardMounted(true)
    setWizardOpen(true)
  }

  // Laisse le bandeau ouvert : lire la nouveauté n'est pas la fermer.
  const handleLearnMore = (): void => {
    captureAnnouncementEvent(
      'learn_more_clicked',
      SMART_PREPARATION_ANNOUNCEMENT
    )
    setWhatsNewOpen(true)
  }

  return (
    <>
      <Collapse in={visible} appear unmountOnExit>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
          <Box
            role="status"
            data-testid="smart-preparation-banner"
            sx={(theme) => ({
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 56,
              py: 1.25,
              pl: { xs: 1.5, sm: 2 },
              pr: { xs: 0.5, sm: 1 },
              borderRadius: '12px',
              color: theme.palette.common.white,
              // Violet de l'Assistant Nabu et de la préparation intelligente : exception au primary du thème.
              backgroundImage: `linear-gradient(280deg, ${darken(
                intelligentPreparationViolet,
                0.45
              )} 0%, ${darken(
                intelligentPreparationViolet,
                0.15
              )} 12%, ${lighten(intelligentPreparationViolet, 0.15)} 35%)`,
              backgroundSize: '135% 100%',
              backgroundPosition: '100% 50%',
              animation: `${gradientBreath} 4s ease-in-out 2`,
              border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
              boxShadow: `0 8px 24px -16px ${alpha(
                darken(intelligentPreparationViolet, 0.45),
                0.55
              )}`,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '25%',
                background: `linear-gradient(90deg, transparent, ${alpha(
                  theme.palette.common.white,
                  0.28
                )}, transparent)`,
                transform: 'translateX(-120%) skewX(-20deg)',
                animation: `${sheen} 2.4s ease-in-out 0.4s 3 forwards`,
                pointerEvents: 'none',
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                '&::after': { display: 'none' },
              },
            })}
          >
            <Box
              aria-hidden
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.common.white, 0.16),
                border: `1px solid ${alpha(theme.palette.common.white, 0.28)}`,
              })}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 1.25,
                rowGap: 0.25,
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={(theme) => ({
                  flexShrink: 0,
                  px: 0.75,
                  py: 0.125,
                  borderRadius: 999,
                  backgroundColor: theme.palette.common.white,
                  color: intelligentPreparationViolet,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  lineHeight: 1.5,
                  textTransform: 'uppercase',
                })}
              >
                {tCommon('smartPreparationBadge')}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'inherit',
                  lineHeight: 1.5,
                  '& strong': { fontWeight: 700 },
                }}
              >
                <Trans
                  i18nKey={tokens.common.smartPreparationAnnouncement}
                  components={{ bold: <strong /> }}
                />
              </Typography>
            </Box>

            {changelog.hasReleases && (
              <Button
                variant="text"
                size="small"
                onClick={handleLearnMore}
                sx={(theme) => ({
                  position: 'relative',
                  zIndex: 1,
                  flexShrink: 0,
                  color: 'inherit',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: alpha(theme.palette.common.white, 0.16),
                  },
                })}
              >
                {tCommon('smartPreparationLearnMore')}
              </Button>
            )}

            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={handleTry}
              sx={(theme) => ({
                position: 'relative',
                zIndex: 1,
                flexShrink: 0,
                backgroundColor: theme.palette.common.white,
                color: intelligentPreparationViolet,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.88),
                },
              })}
            >
              {tCommon('smartPreparationTry')}
            </Button>

            <IconButton
              size="small"
              aria-label={tCommon('close')}
              onClick={handleClose}
              sx={(theme) => ({
                position: 'relative',
                zIndex: 1,
                flexShrink: 0,
                color: 'inherit',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.16),
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.common.white}`,
                  outlineOffset: 1,
                },
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Collapse>

      {/* Hors du Collapse : le wizard reste ouvert une fois le bandeau replié. */}
      {wizardMounted && (
        <DialogUpload
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          defaultPreparation={PreparationMode.Intelligent}
        />
      )}
      <WhatsNewModal
        open={whatsNewOpen}
        releases={changelog.releases}
        isLoading={changelog.isLoading}
        isEmpty={changelog.isEmpty}
        onClose={() => setWhatsNewOpen(false)}
        onAcknowledge={changelog.acknowledgeOnUnderstood}
      />
    </>
  )
}
